/**
 * Jersey Handout Service
 *
 * Jersey status belongs to a registration, not to a class date the way
 * attendance does. A coach reaches a player through a class they are
 * assigned to; that is the only thing the class id is used for.
 */

import apiClient from "../client";
import { API_ENDPOINTS } from "../../constants/api.constants";

const jerseyService = {
  /**
   * The roster for one class: every active player with their jersey size,
   * whether they bought a jersey, and their status.
   *
   * `size_totals` counts only jerseys still owed, so it reads as a packing
   * list rather than a census of the class.
   *
   * @param {string} classId
   * @returns {Promise<{items: Array, size_totals: Object, outstanding_count: number}>}
   */
  async getRoster(classId) {
    const { data } = await apiClient.get(API_ENDPOINTS.JERSEY.ROSTER(classId));
    return data;
  },

  /**
   * Hand a jersey to one player. Safe to call twice: the second call
   * returns the original record rather than issuing a second jersey.
   *
   * Rejects with a 400 when the player never bought one.
   *
   * @param {string} enrollmentId
   * @returns {Promise<Object>} the updated roster row
   */
  async markReceived(enrollmentId) {
    const { data } = await apiClient.post(API_ENDPOINTS.JERSEY.MARK, {
      enrollment_id: enrollmentId,
    });
    return data;
  },

  /**
   * Take back a mark made in error. Coaches have 48 hours from marking;
   * admins are never locked out.
   *
   * @param {string} enrollmentId
   * @returns {Promise<Object>} the updated roster row
   */
  async undo(enrollmentId) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.JERSEY.UNDO(enrollmentId),
    );
    return data;
  },

  /**
   * Ask the office for a different size for one player. The player's
   * registered size is deliberately left alone, so the original order
   * stays on record next to its replacement.
   *
   * @param {string} enrollmentId
   * @param {string} requestedSize - a value from JERSEY_SIZE_OPTIONS
   * @param {string} [note]
   * @returns {Promise<Object>} the updated roster row
   */
  async requestDifferentSize(enrollmentId, requestedSize, note) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.JERSEY.SIZE_REQUEST(enrollmentId),
      { requested_size: requestedSize, note: note || null },
    );
    return data;
  },

  /**
   * Every replacement the office is working on. Admin and owner only.
   * @param {string} [status] - requested, sent_out, received or cancelled
   */
  async getSizeRequests(status) {
    const { data } = await apiClient.get(API_ENDPOINTS.JERSEY.SIZE_REQUESTS, {
      params: status ? { status } : {},
    });
    return data;
  },

  /** The office has posted the replacement. Admin only. */
  async markSizeRequestSent(requestId) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.JERSEY.SIZE_REQUEST_SENT(requestId),
    );
    return data;
  },

  /** The player now has the replacement in their hands. */
  async markSizeRequestReceived(requestId) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.JERSEY.SIZE_REQUEST_RECEIVED(requestId),
    );
    return data;
  },

  /** Admin correction: this replacement should never have been raised. */
  async cancelSizeRequest(requestId) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.JERSEY.SIZE_REQUEST_CANCEL(requestId),
    );
    return data;
  },

  /**
   * Overwrite a player's registered jersey size. Admin only: this is for a
   * size entered wrong at registration, not one the player outgrew.
   *
   * @param {string} enrollmentId
   * @param {string|null} jerseySize - null clears it
   */
  async overrideSize(enrollmentId, jerseySize) {
    const { data } = await apiClient.patch(
      API_ENDPOINTS.JERSEY.OVERRIDE_SIZE(enrollmentId),
      { jersey_size: jerseySize },
    );
    return data;
  },

  /**
   * Club-wide list of jerseys still owed, with the parent contact and
   * address needed to post them. Admin and owner only.
   *
   * @returns {Promise<{items: Array, total: number}>}
   */
  async getOutstanding() {
    const { data } = await apiClient.get(API_ENDPOINTS.JERSEY.OUTSTANDING);
    return data;
  },
};

export default jerseyService;
